// src/profile/follow.ts
//
// Follow / unfollow / request / cancel / approve / reject.
//
// Edges are stored as TWO docs (denormalized) so reads from either
// side are O(1):
//   /users/{target}/followers/{follower_uid}     — "who follows X"
//   /users/{follower}/following/{target_uid}     — "who X follows"
//
// Both edges must be created/deleted atomically. Firestore batched
// writes guarantee atomicity for up to 500 ops, more than enough.
//
// Public-account follow path:
//   1. Batch: create followers/{me} on target, create following/{them} on me.
//   2. Cloud Function bumps target.followers_count and me.following_count.
//
// Private-account follow path:
//   1. Create follow_requests/{me} on target.
//   2. Target sees pending in their requests UI. They EITHER:
//      a. Approve: batch — delete request, create followers/{me}, create following/{them} on me.
//      b. Reject: delete request only.
//   3. Cloud Function bumps counts on approval.
//
// Why client-side batches and not Cloud Functions for the writes:
// rules already enforce that only the right principals can write
// each edge (follower writes their own /following + their own
// /followers entry on the target; target can also write a /followers
// entry as part of approve flow). Doing this client-side keeps
// latency low. Counts are denormalized via Cloud Function triggers
// because clients can't atomically increment a remote profile field
// and create the edge in one transaction (cross-doc writes need
// admin SDK to bump counts on docs the client doesn't own).

import {
  collection,
  doc,
  getDoc,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { db, auth } from '../services/firebase';

/** Best-effort read of target user's account-privacy flag. */
async function isTargetPrivate(targetUid: string): Promise<boolean> {
  try {
    const snap = await getDoc(doc(db, 'users', targetUid));
    if (!snap.exists()) return false;
    return !!(snap.data() as any).is_account_private;
  } catch {
    return false;
  }
}

/**
 * Follow a user. Returns:
 *   - 'following' if the target is public (immediate follow)
 *   - 'requested' if the target is private (request pending)
 *   - 'error' otherwise
 */
export async function followUser(
  targetUid: string
): Promise<'following' | 'requested' | 'error'> {
  const me = auth.currentUser;
  if (!me) return 'error';
  if (me.isAnonymous) return 'error'; // gated by UI before reaching here
  if (me.uid === targetUid) return 'error';

  const targetIsPrivate = await isTargetPrivate(targetUid);

  if (targetIsPrivate) {
    // Pending request only.
    try {
      const reqRef = doc(
        db,
        'users',
        targetUid,
        'follow_requests',
        me.uid
      );
      const batch = writeBatch(db);
      batch.set(reqRef, { requested_at: serverTimestamp() });
      await batch.commit();
      return 'requested';
    } catch (err) {
      console.warn('followUser request failed:', err);
      return 'error';
    }
  }

  // Public account — direct follow.
  try {
    const batch = writeBatch(db);
    batch.set(doc(db, 'users', targetUid, 'followers', me.uid), {
      followed_at: serverTimestamp(),
    });
    batch.set(doc(db, 'users', me.uid, 'following', targetUid), {
      followed_at: serverTimestamp(),
    });
    await batch.commit();
    return 'following';
  } catch (err) {
    console.warn('followUser failed:', err);
    return 'error';
  }
}

/** Unfollow / break the follow relationship. Either side can do
 *  this; from the viewer's perspective it's "unfollow". */
export async function unfollowUser(targetUid: string): Promise<boolean> {
  const me = auth.currentUser;
  if (!me) return false;
  try {
    const batch = writeBatch(db);
    batch.delete(doc(db, 'users', targetUid, 'followers', me.uid));
    batch.delete(doc(db, 'users', me.uid, 'following', targetUid));
    await batch.commit();
    return true;
  } catch (err) {
    console.warn('unfollowUser failed:', err);
    return false;
  }
}

/** Cancel a pending follow request you sent. */
export async function cancelFollowRequest(
  targetUid: string
): Promise<boolean> {
  const me = auth.currentUser;
  if (!me) return false;
  try {
    const ref = doc(db, 'users', targetUid, 'follow_requests', me.uid);
    const batch = writeBatch(db);
    batch.delete(ref);
    await batch.commit();
    return true;
  } catch (err) {
    console.warn('cancelFollowRequest failed:', err);
    return false;
  }
}

/**
 * Approve a follow request someone sent to ME.
 * Atomic: delete request + create both edges.
 */
export async function approveFollowRequest(
  requesterUid: string
): Promise<boolean> {
  const me = auth.currentUser;
  if (!me) return false;
  try {
    const batch = writeBatch(db);
    batch.delete(
      doc(db, 'users', me.uid, 'follow_requests', requesterUid)
    );
    batch.set(doc(db, 'users', me.uid, 'followers', requesterUid), {
      followed_at: serverTimestamp(),
    });
    batch.set(
      doc(db, 'users', requesterUid, 'following', me.uid),
      { followed_at: serverTimestamp() }
    );
    await batch.commit();
    return true;
  } catch (err) {
    console.warn('approveFollowRequest failed:', err);
    return false;
  }
}

/** Reject a pending request — just delete it. */
export async function rejectFollowRequest(
  requesterUid: string
): Promise<boolean> {
  const me = auth.currentUser;
  if (!me) return false;
  try {
    const batch = writeBatch(db);
    batch.delete(
      doc(db, 'users', me.uid, 'follow_requests', requesterUid)
    );
    await batch.commit();
    return true;
  } catch (err) {
    console.warn('rejectFollowRequest failed:', err);
    return false;
  }
}

/**
 * Remove a follower (forcibly unfollow them — the "remove"
 * operation in Instagram). Symmetric to unfollow but initiated by
 * the target. Both edges deleted.
 */
export async function removeFollower(followerUid: string): Promise<boolean> {
  const me = auth.currentUser;
  if (!me) return false;
  try {
    const batch = writeBatch(db);
    batch.delete(doc(db, 'users', me.uid, 'followers', followerUid));
    batch.delete(doc(db, 'users', followerUid, 'following', me.uid));
    await batch.commit();
    return true;
  } catch (err) {
    console.warn('removeFollower failed:', err);
    return false;
  }
}

/** List of users I follow — used to seed the home-feed query in
 *  the non-fan-out fallback path, and to render the following
 *  list on profiles. */
export async function listFollowing(uid: string): Promise<string[]> {
  try {
    const snap = await import('firebase/firestore').then((m) =>
      m.getDocs(collection(db, 'users', uid, 'following'))
    );
    return snap.docs.map((d) => d.id);
  } catch (err) {
    console.warn('listFollowing failed:', err);
    return [];
  }
}
